import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Table,
  Space,
  Button,
  Select,
  DatePicker,
  Typography,
  App,
  Tooltip,
} from 'antd';
import {
  TrophyOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  FileExcelOutlined,
  SearchOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import moment from 'moment';
import { listWinners } from '../../services/competitions';
import {
  listModalities,
  listPeriods,
  listGenders,
} from '../../services/catalogs';
import { exportToExcel } from '../../utils/excel';
import dayjs from 'dayjs';
import usePermissions from '../../hooks/usePermissions';

const { Title } = Typography;
const { useApp } = App;

const CompetitionWinnersList = () => {
  const { message } = useApp();

  const { can } = usePermissions();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });

  const [sorter, setSorter] = useState({});

  // filtros aplicados
  const [modalityId, setModalityId] = useState(null);
  const [periodId, setPeriodId] = useState(null);
  const [genderId, setGenderId] = useState(null);
  const [date, setDate] = useState(null);

  // filtros draft
  const [draftModality, setDraftModality] = useState(null);
  const [draftPeriod, setDraftPeriod] = useState(null);
  const [draftGender, setDraftGender] = useState(null);
  const [draftDate, setDraftDate] = useState(null);

  // catálogos
  const [modalities, setModalities] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [genders, setGenders] = useState([]);

  useEffect(() => {
    Promise.all([
      listModalities(),
      listPeriods(),
      listGenders(),
    ]).then(([m, p, g]) => {
      setModalities(m || []);
      setPeriods(p || []);
      setGenders(g || []);
    });
  }, []);

  const fetchData = useCallback(
    async (
      page,
      pageSize,
      srt,
      mId,
      pId,
      gId,
      d
    ) => {
      setLoading(true);
      try {
        const params = {
          page: page || pagination.current,
          pageSize: pageSize || pagination.pageSize,
          sortField: srt?.field,
          sortOrder: srt?.order,
          ...(mId && { modalityId: mId }),
          ...(pId && { periodId: pId }),
          ...(gId && { genderId: gId }),
          ...(d && { date: d }),
        };

        const { items, total } = await listWinners(params);

        setData(items || []);
        setPagination(prev => ({
          ...prev,
          current: page || prev.current,
          pageSize: pageSize || prev.pageSize,
          total: total || 0,
        }));
      } catch {
        message.error('No se pudieron cargar los ganadores');
      } finally {
        setLoading(false);
      }
    },
    [pagination, message]
  );

  useEffect(() => {
    fetchData(1, pagination.pageSize, null, null, null, null, null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyFilters = ({
    modality = null,
    period = null,
    gender = null,
    date = null,
  } = {}) => {
    setModalityId(modality);
    setPeriodId(period);
    setGenderId(gender);
    setDate(date);

    fetchData(
      1,
      pagination.pageSize,
      sorter,
      modality,
      period,
      gender,
      date
    );
  };

  const handleSearch = () => {
    applyFilters({
      modality: draftModality,
      period: draftPeriod,
      gender: draftGender,
      date: draftDate,
    });
  };

  const handleClearFilters = () => {
    setDraftModality(null);
    setDraftPeriod(null);
    setDraftGender(null);
    setDraftDate(null);
    applyFilters();
  };

  const onTableChange = (pag, filters, srt) => {
    setSorter(srt || {});
    fetchData(
      pag.current,
      pag.pageSize,
      srt,
      modalityId,
      periodId,
      genderId,
      date
    );
  };

  const toggleExpand = (record) => {
    if (!record.videos?.length) return;
    setExpandedRowKeys(prev =>
      prev.includes(record.id)
        ? prev.filter(k => k !== record.id)
        : [...prev, record.id]
    );
  };

  const expandedRowRender = (record) => (
    <Table
      className='winners-table'
      scroll={{ x: 'max-content' }}
      rowKey={(r) => `${record.id}-${r.position}`}
      pagination={false}
      dataSource={[...record.videos].sort(
        (a, b) => a.position - b.position || b.globalScore - a.globalScore
      )}
      columns={[
        {
          title: '',
          width: 40,
          render: (_, __, index) => {
            if (index > 2) return null;

            const colors = ['#BA955E', '#C0C0C0', '#CD7F32']; // oro, plata, bronce

            return (
              <TrophyOutlined
                style={{ color: colors[index], fontSize: 16 }}
              />
            );
          },
        },
        {
          title: 'Lugar',
          dataIndex: 'position',
          width: 80,
          align: 'center',
          render: (_, __, index) => (
            <span>{index + 1}°</span>
          ),
        },
        {
          title: 'Ganador',
          align: 'center',
          render: (_, r) =>
            `${r.participant?.firstName || ''} ${r.participant?.lastName || '' } ${r.participant?.secondLastName || ''}`.trim() ||
            r?.playerName ||
            '-',
        },
        {
          title: 'Puntos',
          align: 'center',
          dataIndex: 'globalScore',
        },
      ]}
    />
  );

  const columns = [
    { title: 'Competencia', dataIndex: 'folio', sorter: true },
    { title: 'Modalidad', dataIndex: 'modalityId', render: (_, r) => r.modality?.name || r.modality.description, sorter: true },
    { title: 'Periodo', dataIndex: 'periodId', render: (_, r) => r.period?.description, sorter: true },
    { title: 'Género', dataIndex: 'genderId', render: (_, r) => r.gender?.description, sorter: true },
    {
      title: 'Fecha',
      dataIndex: 'endDate',
      sorter: true,
      render: d => d ? moment(d).format('DD/MM/YYYY') : '-',
    },
    {
      title: 'Acción',
      render: (_, r) => (
        <Button
          type="link"
          icon={expandedRowKeys.includes(r.id) ? <EyeInvisibleOutlined /> : <EyeOutlined />}
          disabled={!r.videos?.length}
          onClick={() => toggleExpand(r)}
        >
          {expandedRowKeys.includes(r.id) ? 'Ocultar Ganadores' : 'Ver Ganadores'}
        </Button>
      ),
    },
  ];

  const handleExportToExcel = async () => {
    setExporting(true);

    try {
      const { items } = await listWinners({
        page: 1,
        pageSize: 1000,
        modalityId,
        periodId,
        genderId,
        date,
        sortField: sorter?.field,
        sortOrder: sorter?.order,
      });

      if (!items?.length) {
        message.warning('No hay información para exportar');
        return;
      }

      const excelData = [];

      items.forEach((competition) => {
        // Fila principal
        excelData.push({
          Competencia: competition.folio,
          Modalidad: competition.modality?.description || competition.modality?.name,
          Periodo: competition.period?.description,
          Género: competition.gender?.description,
          Fecha: competition.endDate
            ? moment(competition.endDate).format('DD/MM/YYYY')
            : '',
        });

        if (competition.videos?.length) {
          // Encabezado subtabla (inicia en columna B)
          excelData.push({
            Competencia: '',
            Modalidad: 'Lugar',
            Periodo: 'Ganador',
            Género: 'Puntos',
            Fecha: '',
          });

          // Filas de ganadores
          competition.videos.forEach((participant, index) => {
            const medals = ['🥇', '🥈', '🥉'];

            excelData.push({
              Competencia: `${medals[index] || ''}`,
              Modalidad: `${index + 1}°`,
              Periodo:
                `${participant.participant?.firstName || ''} ${participant.participant?.lastName || ''} ${participant.participant?.secondLastName || ''}`.trim() ||
                participant.playerName ||
                '-',
              Género: participant.globalScore,
              Fecha: '',
            });
          });
        }

        // Separador visual
        excelData.push({
          Competencia: '',
          Modalidad: '',
          Periodo: '',
          Género: '',
          Fecha: '',
        });
      });

      exportToExcel(
        excelData,
        [],
        'Ganadores',
        'ganadores_competencias'
      );
    } catch {
      message.error('No se pudo exportar el archivo');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <div className="header-container">
        <Title level={3} style={{ marginBottom: 0 }}>Ganadores</Title>
        <Space>
          {can('winners.export') && (<Tooltip title="Exportar a Excel">
              <Button
                className='btn-add'
                icon={<FileExcelOutlined />}
                onClick={handleExportToExcel}
                loading={exporting}
                disabled={exporting}
                aria-label="Exportar a Excel"
              />
          </Tooltip>)}
        </Space>
      </div>
      <Card>
        <Space wrap style={{ marginBottom: 16 }}>
          <Select
            placeholder="Modalidad"
            allowClear
            style={{ width: 160 }}
            value={draftModality}
            onChange={setDraftModality}
            options={modalities.map(m => ({
              value: m.id,
              label: m.name || m.code,
            }))}
          />
          <Select
            placeholder="Periodo"
            allowClear
            style={{ width: 160 }}
            value={draftPeriod}
            onChange={setDraftPeriod}
            options={periods.map(p => ({
              value: p.id,
              label: p.description || p.code,
            }))}
          />
          <Select
            placeholder="Género"
            allowClear
            style={{ width: 160 }}
            value={draftGender}
            onChange={setDraftGender}
            options={genders.map(g => ({
              value: g.id,
              label: g.description || g.code,
            }))}
          />
          <DatePicker
            placeholder="Fecha"
            value={draftDate ? dayjs(draftDate, 'YYYY-MM-DD') : null}
            onChange={(_, d) => setDraftDate(d)}
          />
          <Tooltip title="Buscar">
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
            />
          </Tooltip>
          <Tooltip title="Limpiar filtros">
            <Button
              icon={<ClearOutlined />}
              onClick={handleClearFilters}
            />
          </Tooltip>
        </Space>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={data}
          loading={loading}
          scroll={{ x: 'max-content' }}
          expandable={{
            expandedRowKeys,
            expandedRowRender,
            rowExpandable: r => r.videos?.length > 0,
            expandIcon: () => null,
          }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
            showTotal: t => `Total: ${t} competencias`,
          }}
          onChange={onTableChange}
        />
      </Card>
    </div>
  );
};

export default CompetitionWinnersList;
